import React, { useEffect, useState } from 'react'

import {
  PhoneOutlined,
  WhatsAppOutlined,
  RightOutlined
} from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Radio,
  Result,
  Row,
  Skeleton,
  Steps
} from 'antd'

// Store
import { useDispatch, useSelector } from 'react-redux'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { addHours, format, formatDistanceToNow, isValid } from 'date-fns'
import formatDate from 'date-fns/format'
import { format as formatRUT } from 'react-rut'
import { es } from 'date-fns/locale'

import { setCurrentPatient, setDrawerActivities } from '../../Store/Actions'
import { PATIENT_CONTACTS, PATIENT_RELEASE, PATIENT_VIEW } from './connection'
import { SvgInline } from '../../Components/SvgInline/SvgInline'
import { DO_ACTIVITY } from '../PatientActivity/connection'
import { calcDV, useMobile } from '../../Utils'

import './PatientView.scss'
import './CallScreen.scss'

import { GET_LOCATIONS } from '../RegisterPatient/connection'
import { UPDATE_PROFILE } from '../../Constants'

import Constants from './constants'

type SelectorPatientViewProps = {
  userInfo: any
  permissions: string[]
  locations: any[]
  token: string
  releaseEvents: any[]
}

type ActivityType = {
  id: number
  name: string
  description: string
  icon: string
}

type AnswerType = {
  id: number
  answer: string
  ActivityField: {
    id: number
    description: string
    name: string
    typeData?: string[]
    typeValue: 'select' | 'text' | 'textarea'
  }
}

type PatientActivityType = {
  Activity: ActivityType
  Answers: AnswerType[]
  id: number
  createdAt: Date
  Authorizing: {
    id: number
    firstName: string
    lastName: string
    rut: string
  }
}

type CardType = {
  id: number
  comments?: string
  statusId?: number
  statusImage?: string
  Activities: PatientActivityType[]
  Person: {
    firstName: string
    lastName: string
    surName: string
    socialName: string
    rut: any
    birthDay?: Date
    age?: number
  }
  PatientLocation: {
    ingressDate: Date
    releaseDate?: Date
    DefReleaseEvent?: {
      id: number
      name: string
    }
    Location: {
      id: number
      code: string
      name: string
    }
  }
  PatientGuardian: {
    Relationship: {
      name: string
    }
    Guardian: {
      firstName: string
      lastName: string
      surName: string
      socialName: string
      Contact?: {
        id?: number
        phone?: string
        email?: string
        isWhatsApp?: boolean
      }
    }
  }
}

type ContactsType = {
  phones: any[]
  whatsApp: any[]
}

type CallScreenState = {
  visible: boolean
  name?: string
  phone?: string
  relation?: string
}

function PatientView({ isMobile, exposeSubMenu, setMainLoading }: any) {
  const { token, userInfo, permissions, releaseEvents, locations } =
    useSelector((state: SelectorPatientViewProps) => state)

  const canActivity = {
    view: permissions.includes('activity:view'),
    add: permissions.includes('activity:add'),
    release: permissions.includes('patient:release')
  }

  const [releaseModal, setReleaseModal] = useState(false)
  const [releaseEventId, setReleaseEventId] = useState<number | null>(null)
  const [_preserveIngressDate, setPreserveIngressDate] = useState<boolean>(true)

  const [modalMultiple, setModalMultiple] = useState<boolean>(false)
  const [contacts, setContacts] = useState<ContactsType>({
    phones: [],
    whatsApp: []
  })
  const [showModalMultiple, setShowModalMultiple] = useState<
    'whatsApp' | 'phones' | null
  >(null)
  const [callLink, setCallLink] = useState<string | null>(null)

  const { patientId } = useParams<any>()

  const [patient, setPatient] = useState<CardType>()

  const [form] = Form.useForm()

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [callScreen, setCallScreen] = useState<CallScreenState>({
    visible: false
  })

  /** Apollo Request */
  const e_PatientView = useQuery(PATIENT_VIEW, {
    context: {
      headers: {
        authorization: `Bearer ${token}`
      }
    },
    variables: {
      PatientViewInput: {
        patientId: Number(patientId)
      }
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  const e_PatientContacts = useQuery(PATIENT_CONTACTS, {
    context: {
      headers: {
        authorization: `Bearer ${token}`
      }
    },
    variables: {
      PatientGetContactsInput: {
        patientId: Number(patientId)
      }
    },
    fetchPolicy: 'no-cache'
  })

  const [getLocations, e_GetLocationTree] = useLazyQuery(GET_LOCATIONS, {
    context: {
      headers: {
        authorization: `Bearer ${token}`
      }
    },
    variables: {
      GetLocationTreeInput: {
        final: true
      }
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  const [doActivity, e_PatientActivity] = useMutation(DO_ACTIVITY, {
    context: {
      headers: {
        authorization: `Bearer ${token}`
      }
    }
  })

  const [release, e_PatientRelease] = useMutation(PATIENT_RELEASE, {
    context: {
      headers: {
        authorization: `Bearer ${token}`
      }
    },
    onCompleted: (data) => {
      if (data?.PatientRelease?.success) {
        if (data.PatientRelease.state.indexOf('PATIENT_TRANSFERRED_TO') === 0) {
          const [habitacion] = data.PatientRelease.state.split('::').reverse()
          message.success(
            `Paciente trasladado exitosamente a: ${habitacion}`,
            10
          )
          e_PatientView.refetch()
          setReleaseModal(false)
          setReleaseEventId(null)
        } else {
          message.success('Cama liberada con éxito.', 5)
          // @ts-ignore
          if (location?.state?.goBackUrl) {
            // @ts-ignore
            history.push(location?.state?.goBackUrl)
          } else {
            history.push('/dashboard')
          }
        }
      }
    },
    onError: (err) => {
      message.error(err.message)
      setReleaseModal(false)
      setReleaseEventId(null)
    }
  })

  useEffect(() => {
    switch (releaseEventId) {
      /** Consultar ubicaciones libres, mostrar select y bloquear confirmar hasta tener el select válido. */
      case Constants.RELEASE_EVENT_TRASLADO_INTERNO:
        getLocations()
        setModalBtn(false)
        break
      /** Mostrar textarea y bloquear confirmar hasta tener al menos un caracter. */
      case Constants.RELEASE_EVENT_TRASLADO_BLOQUEO:
        setModalBtn(false)
        break
      /** Comportamiento esperado habitual */
      case Constants.RELEASE_EVENT_ALTA_MEDICA:
      case Constants.RELEASE_EVENT_FALLECIMIENTO:
      default:
        setModalBtn(true)
        break
    }
    form.resetFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseEventId])

  useEffect(() => {
    if (e_GetLocationTree.data?.HandlersGetLocationTree?.success) {
      console.log(e_GetLocationTree.data.HandlersGetLocationTree.location)
      dispatch({
        type: UPDATE_PROFILE,
        payload: {
          locations: e_GetLocationTree.data.HandlersGetLocationTree.location
        }
      })
    }
  }, [e_GetLocationTree.data, dispatch])

  /** Watchers and Effects */
  useEffect(() => {
    dispatch(setCurrentPatient(patient))
  }, [dispatch, patient])

  useEffect(() => {
    setMainLoading(
      e_PatientView.loading ||
        e_PatientActivity.loading ||
        e_PatientContacts.loading ||
        e_GetLocationTree.loading
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    e_PatientView.loading,
    e_PatientActivity.loading,
    e_PatientContacts.loading,
    e_GetLocationTree.loading
  ])

  useEffect(() => {
    if (e_PatientActivity.data?.PatientDoActivity?.success) {
      let { extra, state } = e_PatientActivity.data.PatientDoActivity
      if (state === 'OK_CALL_SCREEN' && extra) {
        extra = JSON.parse(extra)

        setCallScreen({
          visible: true,
          name: extra.callScreen.name,
          phone: extra.callScreen.phone,
          relation: extra.callScreen.relacion
        })
      }

      setTimeout(() => {
        e_PatientView.refetch({
          PatientViewInput: {
            patientId: Number(patientId)
          }
        })
      }, 300)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e_PatientActivity.data])

  useEffect(() => {
    const { data } = e_PatientView
    if (data?.PatientView?.success) {
      setPatient(data.PatientView.Patient)
      setModalMultiple(data.PatientView.multipleGuardians || false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e_PatientView.data])

  useEffect(() => {
    const { data } = e_PatientContacts
    if (data?.PatientGetContacts?.success) {
      const { withPhone, withWhatsApp } = data.PatientGetContacts
      setContacts({
        phones: withPhone,
        whatsApp: withWhatsApp
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e_PatientContacts.data])

  const [modalBtn, setModalBtn] = useState<boolean>(true)

  if (e_PatientView.loading) {
    return (
      <div className='App'>
        <div className='PatientView'>
          <div className='Wrapper'>
            <Skeleton loading active />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='App'>
      <div className='PatientView'>
        <div className='Wrapper'>
          <CallScreen callScreen={callScreen} setCallScreen={setCallScreen} />
          <Modal
            title={`Contacto: ${
              showModalMultiple === 'whatsApp' ? 'WhatsApp' : 'Teléfono'
            }`}
            className='ContactoModal'
            visible={showModalMultiple !== null}
            onOk={() => {
              let _contact: any =
                showModalMultiple === 'whatsApp'
                  ? contacts.whatsApp
                  : contacts.phones

              _contact = _contact.find((item: any) => item.link === callLink)

              if (_contact) {
                // @ts-ignore
                window.open(callLink, '_blank').focus()
                if (showModalMultiple === 'whatsApp') {
                  doActivity({
                    variables: {
                      PatientDoActivityInput: {
                        patientId: patient?.id,
                        activityId: 8,
                        authorizingId: userInfo?.Profile?.id,
                        clientId: 1,
                        Answers: [
                          {
                            activityFieldId: 10,
                            activityId: 8,
                            answer: `Contacto vía WhatsApp a ${_contact.relation} (${_contact.name}).`,
                            clientId: 1,
                            patientId: patient?.id,
                            reporterUserId: userInfo?.Profile?.id
                          },
                          {
                            activityFieldId: 18,
                            activityId: 8,
                            answer: 'WhatsApp desde app OIRSContigo.',
                            clientId: 1,
                            patientId: patient?.id,
                            reporterUserId: userInfo?.Profile?.id
                          }
                        ]
                      }
                    }
                  })
                } else if (showModalMultiple === 'phones') {
                  doActivity({
                    variables: {
                      PatientDoActivityInput: {
                        patientId: patient?.id,
                        activityId: 2,
                        authorizingId: userInfo?.Profile?.id,
                        clientId: 1,
                        Answers: [
                          {
                            activityFieldId: 12,
                            activityId: 2,
                            answer: `Llamada desde app OIRSContigo a ${_contact.relation} (${_contact.name}).`,
                            clientId: 1,
                            patientId: patient?.id,
                            reporterUserId: userInfo?.Profile?.id
                          }
                        ]
                      }
                    }
                  })
                }
                setTimeout(() => {
                  setShowModalMultiple(null)
                  setCallLink(null)
                }, 1000)
              }
            }}
            onCancel={() => {
              setShowModalMultiple(null)
              setCallLink(null)
            }}
            okText='Llamar'
            cancelText='Volver'
            okButtonProps={{
              disabled: callLink === null,
              loading: e_PatientRelease.loading
            }}
            // onOk={() => {
            //   release({
            //     variables: {
            //       PatientReleaseInput: {
            //         patientId: patient?.id,
            //         releaseEventId
            //       }
            //     }
            //   })
            // }}
          >
            <p>Selecciona un contacto para realizar un contacto.</p>
            <List
              size='small'
              bordered
              dataSource={
                showModalMultiple === 'whatsApp'
                  ? contacts.whatsApp
                  : contacts.phones
              }
              className='ListReleaseEvents'
              renderItem={(item, index) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      callLink === item.link ? 'rgb(0 105 184 / 20%)' : '#FFF',
                    width: '100%',
                    flex: '1 1 100%'
                  }}
                  onClick={() => setCallLink(item.link)}
                >
                  <Radio
                    checked={callLink === item.link}
                    className='ListItemRadio'
                  >
                    <strong>
                      {item.name} - {item.relation}
                      <br />
                      <small>{item.formatted}</small>
                    </strong>
                  </Radio>
                </List.Item>
              )}
            />
          </Modal>
          <Row>
            <Col span={4} md={2}>
              <div className='Icon'>
                {patient?.statusImage ? <SvgInline url={patient.statusImage} /> : <svg
                  width='53'
                  height='53'
                  viewBox='0 0 53 53'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M39.1978 4.41667C39.8503 4.41667 40.4965 4.54519 41.0993 4.79489C41.7021 5.04459 42.2499 5.41059 42.7113 5.87198C43.1727 6.33337 43.5387 6.88112 43.7884 7.48396C44.0381 8.08679 44.1666 8.73291 44.1666 9.38542V43.6256C44.1666 44.2781 44.0381 44.9242 43.7884 45.5271C43.5387 46.1299 43.1727 46.6777 42.7113 47.1391C42.2499 47.6005 41.7021 47.9664 41.0993 48.2162C40.4965 48.4659 39.8503 48.5944 39.1978 48.5944H13.802C13.1495 48.5944 12.5034 48.4659 11.9005 48.2162C11.2977 47.9664 10.75 47.6005 10.2886 47.1391C9.82717 46.6777 9.46118 46.1299 9.21148 45.5271C8.96177 44.9242 8.83325 44.2781 8.83325 43.6256V9.38542C8.8331 8.12639 9.3109 6.91424 10.1701 5.99396C11.0293 5.07368 12.2058 4.51388 13.4619 4.42771L13.802 4.41667H39.1978ZM40.8541 35.3333H12.1458V43.6256C12.1458 44.5399 12.8878 45.2819 13.802 45.2819H39.1978C39.6371 45.2819 40.0584 45.1074 40.369 44.7968C40.6796 44.4862 40.8541 44.0649 40.8541 43.6256V35.3333ZM17.1167 38.6458H35.8853C36.305 38.646 36.7089 38.8054 37.0155 39.0919C37.3222 39.3783 37.5086 39.7705 37.5372 40.1892C37.5658 40.6078 37.4344 41.0218 37.1696 41.3473C36.9048 41.6728 36.5263 41.8857 36.1106 41.9429L35.8853 41.9583H17.1167C16.6973 41.9576 16.2937 41.7978 15.9875 41.5112C15.6813 41.2245 15.4952 40.8324 15.4669 40.4139C15.4386 39.9954 15.5702 39.5817 15.835 39.2564C16.0998 38.9312 16.4781 38.7185 16.8937 38.6613L17.1167 38.6458H35.8853H17.1167ZM39.1978 7.72917H13.802L13.5768 7.74462C13.1802 7.79906 12.8168 7.99523 12.5538 8.29686C12.2907 8.59849 12.1458 8.98518 12.1458 9.38542V32.0208H17.6666V27.041C17.6666 25.6122 18.7531 24.4374 20.1443 24.2961L20.427 24.2806H32.5728C34.0016 24.2806 35.1787 25.3671 35.3178 26.7606L35.3333 27.041V32.0208H40.8541V9.38542C40.8541 8.94615 40.6796 8.52488 40.369 8.21427C40.0584 7.90366 39.6371 7.72917 39.1978 7.72917ZM32.0208 27.5931H20.9791V32.0208H32.0208V27.5931ZM26.4999 11.0306C27.9641 11.0306 29.3684 11.6123 30.4037 12.6476C31.4391 13.683 32.0208 15.0872 32.0208 16.5515C32.0208 18.0157 31.4391 19.4199 30.4037 20.4553C29.3684 21.4906 27.9641 22.0723 26.4999 22.0723C25.0357 22.0723 23.6315 21.4906 22.5961 20.4553C21.5607 19.4199 20.9791 18.0157 20.9791 16.5515C20.9791 15.0872 21.5607 13.683 22.5961 12.6476C23.6315 11.6123 25.0357 11.0306 26.4999 11.0306ZM26.4999 14.3431C25.9142 14.3431 25.3525 14.5758 24.9384 14.9899C24.5242 15.4041 24.2916 15.9658 24.2916 16.5515C24.2916 17.1371 24.5242 17.6988 24.9384 18.113C25.3525 18.5271 25.9142 18.7598 26.4999 18.7598C27.0856 18.7598 27.6473 18.5271 28.0614 18.113C28.4756 17.6988 28.7083 17.1371 28.7083 16.5515C28.7083 15.9658 28.4756 15.4041 28.0614 14.9899C27.6473 14.5758 27.0856 14.3431 26.4999 14.3431Z'
                    fill='#0069B8'
                  />
                </svg>}
              </div>
            </Col>
            <Col
              span={patient?.PatientGuardian?.Guardian?.Contact?.id ? 13 : 20}
            >
              <div className='CardContent'>
                <div className='CardContent-title'>
                  {patient?.Person.firstName}
                  {patient?.Person?.socialName
                    ? ` (${patient?.Person?.socialName}) `
                    : ' '}
                  {patient?.Person.lastName} {patient?.Person.surName}
                </div>
                <div className='CardContent-description'>
                  Ubicación: {patient?.PatientLocation.Location.code}
                </div>
              </div>
            </Col>
            {patient?.PatientGuardian?.Guardian?.Contact?.id &&
              (patient?.PatientGuardian?.Guardian?.Contact?.isWhatsApp ||
                !!patient?.PatientGuardian?.Guardian?.Contact?.phone) && (
                <Col span={7} md={9} className='ContactButtons'>
                  {((modalMultiple && contacts?.whatsApp?.length > 0) ||
                    patient?.PatientGuardian?.Guardian?.Contact
                      ?.isWhatsApp) && (
                    <Button
                      onClick={() => {
                        if (modalMultiple) {
                          // Show modal to select guardian and start WhatsApp
                          setShowModalMultiple('whatsApp')
                        } else {
                          // Start WhatsApp with unique guardian
                          if (
                            patient?.PatientGuardian?.Guardian.Contact?.phone
                          ) {
                            doActivity({
                              variables: {
                                PatientDoActivityInput: {
                                  patientId: patient?.id,
                                  activityId: 8,
                                  authorizingId: userInfo?.Profile?.id,
                                  clientId: 1,
                                  Answers: [
                                    {
                                      activityFieldId: 10,
                                      activityId: 8,
                                      answer: 'Contacto vía WhatsApp.',
                                      clientId: 1,
                                      patientId: patient?.id,
                                      reporterUserId: userInfo?.Profile?.id
                                    },
                                    {
                                      activityFieldId: 18,
                                      activityId: 8,
                                      answer: 'WhatsApp desde app OIRSContigo.',
                                      clientId: 1,
                                      patientId: patient?.id,
                                      reporterUserId: userInfo?.Profile?.id
                                    }
                                  ]
                                }
                              }
                            })

                            // @ts-ignore
                            window
                              .open(
                                `https://wa.me/56${patient?.PatientGuardian?.Guardian.Contact?.phone}`,
                                '_blank'
                              )
                              .focus()
                          }
                        }
                      }}
                      type='primary'
                      shape='circle'
                      icon={<WhatsAppOutlined />}
                      size={'large'}
                    />
                  )}
                  {((modalMultiple && contacts?.phones?.length > 0) ||
                    patient?.PatientGuardian?.Guardian?.Contact?.phone) && (
                    <Button
                      onClick={() => {
                        if (modalMultiple) {
                          // Show modal to select guardian and start call
                          setShowModalMultiple('phones')
                        } else {
                          // Call unique guardian
                          doActivity({
                            variables: {
                              PatientDoActivityInput: {
                                patientId: patient?.id,
                                activityId: 2,
                                authorizingId: userInfo?.Profile?.id,
                                clientId: 1,
                                Answers: [
                                  {
                                    activityFieldId: 12,
                                    activityId: 2,
                                    answer: `Llamada desde app OIRSContigo a ${patient?.PatientGuardian?.Relationship?.name} (${patient?.PatientGuardian?.Guardian?.firstName} ${patient?.PatientGuardian?.Guardian?.lastName} ${patient?.PatientGuardian?.Guardian?.surName}).`,
                                    clientId: 1,
                                    patientId: patient?.id,
                                    reporterUserId: userInfo?.Profile?.id
                                  }
                                ]
                              }
                            }
                          })

                          // @ts-ignore
                          window
                            .open(
                              `tel://+56${patient?.PatientGuardian?.Guardian?.Contact?.phone}`,
                              '_blank'
                            )
                            .focus()
                        }
                      }}
                      type='primary'
                      shape='circle'
                      icon={<PhoneOutlined />}
                      size={'large'}
                    />
                  )}
                </Col>
              )}
          </Row>
          <Row>
            <Col span={24}>
              <Card className='DemographicInfo'>
                <ul>
                  {patient?.PatientLocation.ingressDate &&
                    isValid(new Date(patient?.PatientLocation.ingressDate)) && (
                      <>
                        <li>
                          Ingreso:{' '}
                          <strong>
                            {formatDate(
                              new Date(patient?.PatientLocation.ingressDate),
                              'dd-MM-yyyy'
                            )}
                          </strong>
                          {/* {' '}
                          a las{' '}
                          <strong>
                            {formatDate(
                              new Date(patient?.PatientLocation.ingressDate),
                              'HH:mm:ss'
                            )}
                          </strong> */}
                          <br />
                        </li>
                        <br />
                      </>
                    )}

                  {patient?.Person?.rut && (
                    <li>
                      {' '}
                      RUT:{' '}
                      <strong>
                        {formatRUT(
                          `${patient?.Person?.rut}${calcDV(
                            patient?.Person?.rut
                          )}`
                        )}
                      </strong>{' '}
                    </li>
                  )}
                  {patient?.Person.age && (
                    <li>
                      {' '}
                      Edad:{' '}
                      <strong>
                        {patient?.Person.age} año
                        {patient?.Person.age > 1 ? 's' : ''}
                      </strong>{' '}
                    </li>
                  )}
                  {patient?.Person.birthDay && (
                    <li>
                      {' '}
                      Fecha de Nacimiento:{' '}
                      <strong>
                        {formatDate(
                          new Date(patient?.Person?.birthDay),
                          'dd-MM-yyyy'
                        )}
                      </strong>{' '}
                    </li>
                  )}

                  {(patient?.Person?.rut ||
                    patient?.Person.age ||
                    patient?.Person.birthDay) && <br />}
                  <li>
                    Tutor:{' '}
                    <strong>
                      {patient?.PatientGuardian ? (
                        `${
                          patient?.PatientGuardian?.Guardian.firstName || ''
                        }${' '}
                      ${patient?.PatientGuardian?.Guardian.lastName || ''}${' '}
                      ${patient?.PatientGuardian?.Guardian.surName || ''}`
                      ) : (
                        <>Sin tutor</>
                      )}
                    </strong>
                  </li>
                  <li>
                    Contacto de Tutor:{' '}
                    <strong>
                      {patient?.PatientGuardian?.Guardian?.Contact?.phone ||
                        'Sin número'}
                    </strong>
                  </li>

                  <br />
                  {patient?.comments && (
                    <li className='RedBullet'>
                      Comentarios: <br />
                      <div
                        dangerouslySetInnerHTML={{ __html: patient?.comments }}
                      />
                    </li>
                  )}
                </ul>
              </Card>
            </Col>
          </Row>
          {/* {!patient?.Person?.rut && (
            <Row>
              <Col span={24}>
                <Button
                  type='primary'
                  size='large'
                  danger
                  style={{
                    minHeight: 56,
                    borderRadius: 16
                  }}
                  onClick={() => {
                    // dispatch(setDrawerActivities(true))
                  }}
                  block
                >
                  Asociar Identificación
                </Button>
              </Col>
            </Row>
          )} */}
          {canActivity.add && (
            <Row>
              <Col span={24}>
                <Button
                  type='primary'
                  size='large'
                  className='RegisterActivity'
                  onClick={() => dispatch(setDrawerActivities(true))}
                  block
                >
                  Registrar Actividad
                </Button>
              </Col>
            </Row>
          )}
          {canActivity.view && (
            <Row>
              <Col span={24}>
                <Card title='Registro de Actividades'>
                  {patient?.Activities && patient?.Activities.length > 0 ? (
                    <ActivityList activities={patient?.Activities} />
                  ) : (
                    <Empty description='No hay actividades.' />
                  )}
                </Card>
              </Col>
            </Row>
          )}
          {canActivity.release && <Row>
            <Col span={24}>
              <Button
                type='default'
                danger
                size='large'
                className='ChangeRoom'
                block
                onClick={() => setReleaseModal(true)}
              >
                Liberar cama
              </Button>
            </Col>
          </Row>}
          <Modal
            title='Liberar cama'
            className='LiberarModal'
            visible={releaseModal}
            onCancel={() => {
              setReleaseModal(false)
              setReleaseEventId(null)
              setPreserveIngressDate(true)
            }}
            okText='Confirmar'
            cancelText='Volver'
            cancelButtonProps={{
              style: {
                display: e_PatientRelease.loading ? 'none' : undefined
              }
            }}
            // key={`ModalLiberalCama${modalBtn}`}
            okButtonProps={{
              disabled: releaseEventId === null || !modalBtn,
              loading: e_PatientRelease.loading
            }}
            onOk={async () => {
              try {
                const fields = await form.validateFields()
                
                const PatientReleaseInput: any = {
                  patientId: patient?.id,
                  releaseEventId
                }
                const _vals = Object.entries(form.getFieldsValue())

                
                if (_vals.length > 0) {
                  const [data] = _vals[0].reverse()
                  const extra = data
                  
                  PatientReleaseInput.extra = extra
                }

                if (fields?.preserveIngressDate === false && fields?.ingressDate) {
                  const _date = addHours(new Date(fields.ingressDate), 12)

                  PatientReleaseInput.extra.ingressDate = _date
                }
                
                PatientReleaseInput.extra = JSON.stringify(PatientReleaseInput.extra)
                
                release({
                  variables: {
                    PatientReleaseInput
                  }
                })
              } catch (err) {
                console.log({
                  err
                })
              }
            }}
          >
            {e_PatientRelease.loading ? (
              <Skeleton loading active title round />
            ) : (
              <>
                <p>Selecciona un motivo de liberación de esta cama.</p>
                <List
                  size='small'
                  bordered
                  dataSource={releaseEvents}
                  style={{ marginBottom: 16 }}
                  className='ListReleaseEvents'
                  renderItem={(item, index) => (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        backgroundColor:
                          releaseEventId === item.id
                            ? 'rgb(190 51 49 / 20%)'
                            : '#FFF',
                        width: '100%',
                        flex: '1 1 100%'
                      }}
                      onClick={() => setReleaseEventId(item.id)}
                    >
                      <Radio
                        checked={releaseEventId === item.id}
                        className='ListItemRadio'
                      >
                        <SvgInline url={item.icon} />
                        <strong>
                          {item.name}
                          <br />
                          <small>{item.descrpition}</small>
                        </strong>
                      </Radio>
                    </List.Item>
                  )}
                />

                <datalist
                  id='locationsList'
                  style={{ width: '100%' }}
                  placeholder='ej: Pieza L2S1'
                >
                  {locations.map((loc) => (
                    <option value={loc.code} label={loc.name}>
                      {/* <div className='demo-option-label-item'>
                    {loc.name} - {loc.code}
                    <br />
                    <small>{loc.breadcrumb}</small>
                  </div> */}
                    </option>
                  ))}
                </datalist>

                {e_GetLocationTree.loading ? (
                  <Skeleton loading active title={false} />
                ) : (
                  <Form
                    form={form}
                    layout='vertical'
                    initialValues={{
                      preserveIngressDate: true,
                      ingressDate: patient?.PatientLocation?.ingressDate ? format(new Date(patient?.PatientLocation?.ingressDate), 'yyyy-MM-dd') : undefined
                    }}
                    onFieldsChange={(cf, af) => {
                      if (releaseEventId) {
                        const isFormValid = form
                          .getFieldsError()
                          .some((item) => item.errors.length > 0)
                        setModalBtn(!isFormValid)
                      } else {
                        setModalBtn(false)
                      }
                    }}
                  >
                    {releaseEventId ===
                      Constants.RELEASE_EVENT_TRASLADO_BLOQUEO && (
                      <Form.Item
                        name={[
                          `releaseEventId_${Constants.RELEASE_EVENT_TRASLADO_BLOQUEO}`,
                          'message'
                        ]}
                        label='Comentario de Bloqueo:'
                        rules={[
                          {
                            required: true,
                            message:
                              'Debe ingresar un comentario para bloquear cama.'
                          }
                        ]}
                      >
                        <Input.TextArea showCount maxLength={100} />
                      </Form.Item>
                    )}
                    {releaseEventId ===
                      Constants.RELEASE_EVENT_TRASLADO_INTERNO && (
                      <React.Fragment>
                        <Form.Item
                          name={[
                            `releaseEventId_${Constants.RELEASE_EVENT_TRASLADO_INTERNO}`,
                            'destinyLocationId'
                          ]}
                          label='Ubicación de Destino'
                          required
                          rules={[
                            {
                              required: true,
                              message:
                                'Debe seleccionar una ubicación de traslado.'
                            },
                            {
                              validator: (
                                rule: any,
                                value: any,
                                callback: any
                              ) => {
                                const exists = locations.find(
                                  (loc) => loc.code === value
                                )
                                if (!exists) {
                                  callback(
                                    'La ubicación seleccionada debe ser seleccionada del listado.'
                                  )
                                }
                                callback()
                              }
                            }
                          ]}
                        >
                          <Input
                            size='large'
                            list='locationsList'
                            prefix={`Cama`}
                          />
                        </Form.Item>
                        <Form.Item
                          name='preserveIngressDate'
                          valuePropName='checked'
                          // wrapperCol={{ offset: 8, span: 16 }}
                        >
                          <Checkbox onChange={() => {
                            setPreserveIngressDate(form.getFieldValue('preserveIngressDate'))
                          }}>Mantener fecha de ingreso original {patient?.PatientLocation?.ingressDate && <>({format(new Date(patient?.PatientLocation?.ingressDate), 'dd-MM-yyyy')})</>}</Checkbox>
                        </Form.Item>
                        {!_preserveIngressDate && <Form.Item
                          name={'ingressDate'}
                          label='Modifica fecha de ingreso'
                          required
                          rules={[
                            {
                              required: true,
                              message:
                                'Si modificas la fecha de ingreso original, debes ingresar una nueva.'
                            }
                          ]}
                        >
                          <Input type='date' />
                        </Form.Item>}
                      </React.Fragment>
                    )}
                  </Form>
                )}
              </>
            )}
          </Modal>
        </div>
      </div>
    </div>
  )
}

type ActivityListProps = {
  activities: PatientActivityType[]
}

const ActivityList = ({ activities }: ActivityListProps) => {
  const [detail, setDetail] = useState<PatientActivityType>()
  const [modal, setModal] = useState<boolean>(false)
  const isMobile = useMobile()

  const launchModal = (activity: PatientActivityType) => {
    setDetail(activity)
    setModal(true)
  }

  const getDescription = (activity: PatientActivityType) => {
    return (
      <>
        {activity.Answers[0].answer || <i>Sin comentarios</i>}
        <RightOutlined className='EnterActivity' />
      </>
    )
  }

  // activities.reverse()
  return (
    <>
      <Modal
        visible={modal}
        centered
        onCancel={() => setModal(false)}
        onOk={() => setModal(false)}
        title={`Detalle de la actividad`}
        width={'80%'}
      >
        <Descriptions bordered className='DetailActivity' layout={isMobile ? 'horizontal' : 'vertical'} column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
          <Descriptions.Item
            label={
              <>
                <SvgInline url={detail?.Activity.icon!} />
                Tipo de Actividad
              </>
            }
          >
            <strong>{detail?.Activity.name}</strong>
            <br />
            <span>{detail?.Activity.description}</span>
          </Descriptions.Item>
          <Descriptions.Item label='Identificador'>
            {detail?.id}
          </Descriptions.Item>
          <Descriptions.Item label='Fecha de Registro'>
            {formatDate(
              new Date(detail?.createdAt || 0),
              `dd 'de' MMM 'de' yyyy 'a las' HH:mm:ss`,
              { locale: es }
            )}
          </Descriptions.Item>
          {detail?.Authorizing && <Descriptions.Item label='Orientador'>
            {detail?.Authorizing?.firstName} {detail?.Authorizing?.lastName} {detail?.Authorizing?.rut ? <>({formatRUT(`${detail?.Authorizing?.rut}-${calcDV(
                            `${detail?.Authorizing?.rut}`
                          )}`)})</> : null}
          </Descriptions.Item>}
        </Descriptions>

        <Descriptions
          title='Información personalizada'
          bordered
          className='DetailActivity'
          style={{
            marginTop: 24
          }}
        >
          {detail?.Answers.map((answer, index) => (
            <Descriptions.Item label={answer.ActivityField.name} key={index}>
              {answer.answer || <i>Sin información</i>}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Modal>
      <Steps
        current={activities.length}
        direction='vertical'
        className='ActivityList'
      >
        {[...activities].reverse().map((activity, index) => (
          <Steps.Step
            key={`StepsStep${index}`}
            title={activity.Activity.name}
            description={getDescription(activity)}
            subTitle={formatDistanceToNow(new Date(activity.createdAt), {
              locale: es,
              addSuffix: true
            })}
            onClick={() => {
              launchModal(activity)
            }}
            icon={<SvgInline url={activity.Activity.icon} />}
          />
        ))}
      </Steps>
    </>
  )
}

type CallScreenProps = {
  callScreen: CallScreenState
  setCallScreen: (state: CallScreenState) => void
}

const CallScreen = ({ callScreen, setCallScreen }: CallScreenProps) => {
  return (
    <Modal
      visible={callScreen.visible}
      className='CallScreen'
      footer={null}
      onCancel={() => setCallScreen({ visible: false })}
    >
      <Result
        icon={<PhoneOutlined />}
        status='success'
        title={
          <>
            Estás en llamada con <br />
            <strong>{callScreen.name}</strong>
          </>
        }
        subTitle={`${callScreen.phone} - Relacion: ${callScreen.relation}`}
        extra={
          <Button
            type='primary'
            onClick={() => setCallScreen({ visible: false })}
            size='large'
          >
            Finalizar
          </Button>
        }
      />
    </Modal>
  )
}

export default PatientView
